import { useState } from 'react'
import { Button } from "@/components/ui/button"
import { Logo } from "@/components/ui/logo"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { VoiceWidget } from "@/components/voice-widget"
import { Phone, Loader2, MoreHorizontal, LogOut, Home, Settings, Menu, X, Calendar, FileText, Activity, Feather, ChevronDown, AudioWaveform, BotMessageSquare, Palette, Chrome } from "lucide-react"
import { ThemeSelector } from "@/components/theme-selector"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { useAuth } from "@/components/providers/auth-provider"
import { CreatorScoreBadge } from "@/components/analytics/creator-score-badge"
import { useNavigate, useLocation } from "react-router-dom"
import { useMutation, useQuery, useLazyQuery } from "@apollo/client"
import { useToast } from "@/hooks/use-toast"
import { INITIATE_VOICE_CALL, GET_INCOMING_CALL_NUMBER, GET_SIGNED_URL } from "@/lib/graphql/voice"
import { useConversation } from '@elevenlabs/react'
import { useXAuth } from '@/hooks/use-x-auth'

export function AppHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [userCallActive, setUserCallActive] = useState(false)
  const { user, logout, isAuthenticated } = useAuth()
  const { initiateXAuth } = useXAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()
  const [fetchUserSignedUrl] = useLazyQuery(GET_SIGNED_URL, {
    fetchPolicy: 'network-only' // Always fetch fresh signed URL, never use cache
  })
  
  const [initiateCall, { loading: callLoading }] = useMutation(INITIATE_VOICE_CALL, {
    onCompleted: () => {
      toast({
        title: "Call Initiated Successfully!",
        description: "Your call is being connected. Check your phone in a moment.",
      })
    },
    onError: (error) => {
      toast({
        title: "Call Failed",
        description: error.message,
        variant: "destructive"
      })
    }
  })

  // Only for a signed-in account: guests have no voice, and the API refuses them.
  const { data: incomingCallData } = useQuery(GET_INCOMING_CALL_NUMBER, { skip: !isAuthenticated || Boolean((user as any)?.isGuest) })
  const incomingCallNumber = incomingCallData?.getIncomingCallNumber || 'x11.social'
  
  // Initialize ElevenLabs conversation for voice widget
  const conversation = useConversation({
    onConnect: () => {
      console.log('Connected to ElevenLabs')
      toast({
        title: "Connected",
        description: "You're now connected to our AI assistant. Start speaking!",
      })
    },
    onDisconnect: () => {
      console.log('Disconnected from ElevenLabs')
      setUserCallActive(false)
      toast({
        title: "Call Ended",
        description: "Your voice call has ended.",
      })
    },
    onError: (error) => {
      console.error('ElevenLabs error:', error)
      setUserCallActive(false)
      toast({
        title: "Connection Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive"
      })
    }
  })

  const handleCallNow = async () => {
    if (!user?.phoneVerified) {
      toast({
        title: "Phone Verification Required",
        description: "Please verify your phone number before making calls",
        variant: "destructive"
      })
      return
    }

    if (!user?.credits || user.credits < 1) {
      toast({
        title: "Insufficient Credits",
        description: "You need at least 1 minute of credits to make a call",
        variant: "destructive"
      })
      return
    }

    await initiateCall()
  }
  
  // Handle voice widget call (browser-based)
  const handleVoiceWidget = async () => {
    // End any existing session first
    if (conversation.status === 'connected') {
      await conversation.endSession()
    }
    
    setUserCallActive(true)
    
    try {
      // Get signed URL for authenticated user
      const { data } = await fetchUserSignedUrl()
      
      if (data?.getSignedUrl) {
        // Connect to ElevenLabs with the signed URL and user_id
        await conversation.startSession({
          signedUrl: data.getSignedUrl,
          dynamicVariables: {
            user_id: user?.id || ''  // Pass the MongoDB user ID
          }
        })
      } else {
        throw new Error('No signed URL received')
      }
    } catch (error: any) {
      console.error('Failed to start voice call:', error)
      setUserCallActive(false)
      
      const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || 'Please try again later.'
      const isInsufficientCredits = errorMessage.toLowerCase().includes('insufficient credits')
      
      toast({
        title: isInsufficientCredits ? "Insufficient Credits" : "Failed to Start Call",
        description: errorMessage,
        variant: "destructive"
      })
    }
  }
  
  // Handle ending the user call
  const handleEndUserCall = async () => {
    if (conversation.status === 'connected') {
      await conversation.endSession()
    }
    setUserCallActive(false)
  }

  // Determine navigation button based on current page
  const isOnAnalytics = location.pathname === '/analytics'
  const navigationTarget = isOnAnalytics ? '/' : '/analytics'
  const navigationLabel = isOnAnalytics ? 'Home' : 'Analytics'

  const handleSignIn = () => {
    initiateXAuth()
  }

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <div className="px-4 md:px-6 py-4 md:py-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between">
        <button 
          onClick={() => navigate(user ? '/chat' : '/')}
          className="hover:opacity-75 transition-opacity"
        >
          <Logo variant="header" />
        </button>
        
        {isAuthenticated ? (
          <>
        {/* Desktop Navigation - Authenticated */}
        <div className="hidden md:flex items-start gap-4">
          <div className="flex flex-col items-center gap-1">
            <DropdownMenu>
              <div className="flex items-center gap-0">
                <Button 
                  onClick={handleVoiceWidget}
                  disabled={callLoading}
                  className="bg-foreground hover:bg-foreground/90 text-background rounded-r-none"
                >
                  {callLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <BotMessageSquare className="w-4 h-4 mr-2" />
                      Voice Assistant
                    </>
                  )}
                </Button>
                <DropdownMenuTrigger asChild>
                  <Button
                    disabled={callLoading}
                    className="bg-foreground hover:bg-foreground/90 text-background rounded-l-none border-l border-background/20 px-2"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
              </div>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={handleVoiceWidget}>
                  <AudioWaveform className="w-4 h-4 mr-2" />
                  Direct Convo
                  <span className="ml-auto text-xs text-muted-foreground">Default</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleCallNow}>
                  <Phone className="w-4 h-4 mr-2" />
                  Phone Call
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <span>Call to tweet:</span>
                    <span className="font-semibold text-foreground">{incomingCallNumber}</span>
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/chat')}
            className="hover:bg-accent"
          >
            <Feather className="w-4 h-4 lg:mr-2" />
            <span className="hidden lg:inline">Creator Chat</span>
          </Button>
          <Button variant="outline" onClick={() => navigate(navigationTarget)}>
            {isOnAnalytics ? (
              <>
                <Home className="w-4 h-4 lg:mr-2" />
                <span className="hidden lg:inline">{navigationLabel}</span>
              </>
            ) : (
              <>
                <Activity className="w-4 h-4 lg:mr-2" />
                <span className="hidden lg:inline">{navigationLabel}</span>
              </>
            )}
          </Button>
          <Button variant="outline" onClick={() => navigate('/styles')}>
            <Palette className="w-4 h-4 lg:mr-2" />
            <span className="hidden lg:inline">Writing Style</span>
          </Button>
          <ThemeSelector />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 p-2 rounded-full hover:bg-accent transition-colors">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.xProfileImage} alt={user?.xUsername} />
                  <AvatarFallback className="bg-foreground text-background text-sm font-bold">
                    {(user?.xDisplayName || user?.xUsername || 'U').charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col items-start">
                  <span className="text-sm font-bold text-foreground leading-none">
                    {user?.xDisplayName || user?.xUsername}
                  </span>
                  <span className="text-xs text-muted-foreground leading-none">
                    @{user?.xUsername}
                  </span>
                </div>
                <CreatorScoreBadge score={user?.creatorScore} variant="small" />
                <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate('/analytics')}>
                <Activity className="w-4 h-4 mr-2" />
                Analytics
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <Settings className="w-4 h-4 mr-2" />
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => window.open('https://chromewebstore.google.com/detail/fnpbcidkcfbbijjnibgfhdablepmmefk', '_blank')}>
                <Chrome className="w-4 h-4 mr-2" />
                Chrome Extension
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                Sign out @{user?.xUsername}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Mobile Navigation - Authenticated */}
        <div className="md:hidden flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                disabled={callLoading}
                size="icon"
                className="bg-foreground hover:bg-foreground/90 text-background h-9 w-9"
              >
                {callLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <BotMessageSquare className="w-4 h-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={handleVoiceWidget}>
                <AudioWaveform className="w-4 h-4 mr-2" />
                Direct Convo
                <span className="ml-auto text-xs text-muted-foreground">Default</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleCallNow}>
                <Phone className="w-4 h-4 mr-2" />
                Phone Call
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <span>Call to tweet:</span>
                  <span className="font-semibold text-foreground">{incomingCallNumber}</span>
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button 
            onClick={() => navigate('/chat')}
            size="icon"
            variant="outline"
            className="hover:bg-accent h-9 w-9"
          >
            <Feather className="w-4 h-4" />
          </Button>
          <ThemeSelector />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="h-10 w-10"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
          </>
        ) : (
          <>
            {/* Desktop layout - Not Authenticated */}
            <div className="hidden md:flex items-center gap-2">
              <Button 
                variant="ghost" 
                className="text-muted-foreground hover:text-foreground"
                onClick={() => window.open('/blog/', '_self')}
              >
                Blog
              </Button>
              <ThemeSelector />
              <Button 
                variant="ghost" 
                className="text-muted-foreground hover:text-foreground flex items-center gap-2"
                onClick={handleSignIn}
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                Sign in
              </Button>
            </div>

            {/* Mobile Sign In and Theme - Not Authenticated */}
            <div className="md:hidden flex items-center gap-2">
              <ThemeSelector />
              <Button 
                variant="ghost" 
                size="sm"
                className="text-muted-foreground hover:text-foreground flex items-center gap-2"
                onClick={handleSignIn}
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                Sign in
              </Button>
            </div>
          </>
        )}
            </div>
          </div>

          {/* Mobile Menu Dropdown - Only show for authenticated users */}
          {isAuthenticated && mobileMenuOpen && (
            <div className="md:hidden mt-4 py-4 border-t border-border/50">
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-2">
              <span className="text-xs text-muted-foreground">Call to tweet:</span>
              <span className="text-sm font-bold text-foreground">{incomingCallNumber}</span>
            </div>
            
            <div className="px-2 space-y-2">
              <Button 
                variant="ghost" 
                className="w-full justify-start text-muted-foreground hover:text-foreground"
                onClick={() => {
                  handleVoiceWidget()
                  setMobileMenuOpen(false)
                }}
              >
                <AudioWaveform className="w-4 h-4 mr-2" />
                Direct Convo
              </Button>
              
              <Button 
                variant="ghost" 
                className="w-full justify-start text-muted-foreground hover:text-foreground"
                onClick={() => {
                  handleCallNow()
                  setMobileMenuOpen(false)
                }}
              >
                <Phone className="w-4 h-4 mr-2" />
                Phone Call
              </Button>
            </div>
            
            <div className="border-t border-border pt-4"></div>
            
            <Button 
              variant="ghost" 
              className="w-full justify-start text-muted-foreground hover:text-foreground"
              onClick={() => {
                navigate('/')
                setMobileMenuOpen(false)
              }}
            >
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>

            <Button 
              variant="ghost" 
              className="w-full justify-start text-muted-foreground hover:text-foreground"
              onClick={() => {
                navigate('/analytics')
                setMobileMenuOpen(false)
              }}
            >
              <Activity className="w-4 h-4 mr-2" />
              Analytics
            </Button>

            <Button 
              variant="ghost" 
              className="w-full justify-start text-muted-foreground hover:text-foreground"
              onClick={() => {
                navigate('/scheduler')
                setMobileMenuOpen(false)
              }}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Recurring Calls
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-foreground"
              onClick={() => {
                navigate('/drafts')
                setMobileMenuOpen(false)
              }}
            >
              <FileText className="w-4 h-4 mr-2" />
              Schedule Posts
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-foreground"
              onClick={() => {
                navigate('/chat')
                setMobileMenuOpen(false)
              }}
            >
              <Feather className="w-4 h-4 mr-2" />
              Creator Chat
            </Button>

            <Button
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-foreground"
              onClick={() => {
                navigate('/analytics')
                setMobileMenuOpen(false)
              }}
            >
              <Activity className="w-4 h-4 mr-2" />
              Analytics
            </Button>

            <div className="border-t border-border pt-4">
              <div className="flex items-center gap-3 px-2 mb-3">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.xProfileImage} alt={user?.xUsername} />
                  <AvatarFallback className="bg-foreground text-background text-sm font-bold">
                    {(user?.xDisplayName || user?.xUsername || 'U').charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-foreground">
                    {user?.xDisplayName || user?.xUsername}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    @{user?.xUsername}
                  </span>
                </div>
              </div>
              
              <Button
                variant="ghost"
                className="w-full justify-start text-muted-foreground hover:text-foreground"
                onClick={() => {
                  navigate('/profile')
                  setMobileMenuOpen(false)
                }}
              >
                <Settings className="w-4 h-4 mr-2" />
                Profile Settings
              </Button>

              <Button
                variant="ghost"
                className="w-full justify-start text-muted-foreground hover:text-foreground"
                onClick={() => {
                  window.open('https://chromewebstore.google.com/detail/fnpbcidkcfbbijjnibgfhdablepmmefk', '_blank')
                  setMobileMenuOpen(false)
                }}
              >
                <Chrome className="w-4 h-4 mr-2" />
                Chrome Extension
              </Button>

              <Button
                variant="ghost"
                className="w-full justify-start text-destructive hover:text-destructive/90"
                onClick={() => {
                  logout()
                  setMobileMenuOpen(false)
                }}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </Button>
            </div>
          </div>
        </div>
      )}
        </div>
      </header>
    
    {/* Voice Widget for authenticated users */}
    {isAuthenticated && (
      <VoiceWidget
        isActive={userCallActive && conversation.status === 'connected'}
        isSpeaking={conversation.isSpeaking}
        onStop={handleEndUserCall}
        onCancel={handleEndUserCall}
        isDemo={false}
        demoMinutesRemaining={user?.credits}
        getInputByteFrequencyData={conversation.getInputByteFrequencyData}
        getOutputByteFrequencyData={conversation.getOutputByteFrequencyData}
      />
    )}
    </>
  )
}
